import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import IncidentCaseManagement from './components/IncidentCaseManagement';
import EventTimeline from './components/EventTimeline';
import ThreatIntelligence from './components/ThreatIntelligence';
import UserBehaviorAnalysis from './components/UserBehaviorAnalysis';
import EvidenceCollection from './components/EvidenceCollection';
import CorrelationAnalysis from './components/CorrelationAnalysis';
import { auditTrailService } from '../../lib/auditTrailService';

const SecurityEventInvestigationPortal = () => {
  const { userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('cases');
  const [selectedCase, setSelectedCase] = useState(null);
  const [securityEvents, setSecurityEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    openCases: 0,
    criticalEvents: 0,
    unresolvedThreats: 0,
    averageResolutionTime: 0
  });

  useEffect(() => {
    loadSecurityData();
  }, []);

  const loadSecurityData = async () => {
    setLoading(true);
    try {
      const { data: events, error } = await auditTrailService?.getSecurityEvents({
        resolved: false,
        limit: 100
      });

      if (!error && events) {
        setSecurityEvents(events);
        
        // Calculate statistics
        const criticalCount = events?.filter(e => e?.severity === 'critical')?.length;
        const openCasesCount = events?.filter(e => !e?.resolved)?.length;
        
        setStats({
          openCases: openCasesCount,
          criticalEvents: criticalCount,
          unresolvedThreats: events?.filter(e => e?.requires_immediate_action)?.length,
          averageResolutionTime: 4.2 // Mock calculation
        });
      }
    } catch (error) {
      console.error('Error loading security data:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'cases', label: 'Incident Cases', icon: 'FolderOpen' },
    { id: 'timeline', label: 'Event Timeline', icon: 'Clock' },
    { id: 'threats', label: 'Threat Intelligence', icon: 'Shield' },
    { id: 'behavior', label: 'User Behavior', icon: 'Users' },
    { id: 'evidence', label: 'Evidence Collection', icon: 'FileText' },
    { id: 'correlation', label: 'Correlation Analysis', icon: 'Network' }
  ];

  const statCards = [
    { label: 'Open Cases', value: stats?.openCases, icon: 'AlertTriangle', color: 'var(--color-warning)' },
    { label: 'Critical Events', value: stats?.criticalEvents, icon: 'AlertOctagon', color: 'var(--color-error)' },
    { label: 'Unresolved Threats', value: stats?.unresolvedThreats, icon: 'Shield', color: 'var(--color-error)' },
    { label: 'Avg Resolution (hrs)', value: stats?.averageResolutionTime, icon: 'Clock', color: 'var(--color-info)' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <div className="page-content-wrapper">
        <div className="container mx-auto px-4 lg:px-8 py-8">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-2">
                Security Event Investigation Portal
              </h1>
              <p className="text-muted-foreground">
                Forensic analysis and incident response for security threats
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={loadSecurityData}>
                <Icon name="RefreshCw" size={16} className="mr-2" />
                Refresh
              </Button>
              <Button variant="default" size="sm">
                <Icon name="Plus" size={16} className="mr-2" />
                New Investigation
              </Button>
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {statCards?.map((stat, index) => (
              <div key={index} className="card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">{stat?.label}</p>
                    <p className="text-3xl font-heading font-bold text-foreground">
                      {stat?.value}
                    </p>
                  </div>
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${stat?.color}15` }}
                  >
                    <Icon name={stat?.icon} size={24} color={stat?.color} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Tab Navigation */}
          <div className="bg-card rounded-lg border border-border mb-6">
            <div className="flex items-center gap-2 p-2 overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-250 whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {loading ? (
              <div className="card">
                <div className="flex items-center justify-center py-12">
                  <Icon name="Loader" size={32} className="animate-spin text-primary" />
                  <span className="ml-3 text-muted-foreground">Loading security data...</span>
                </div>
              </div>
            ) : (
              <>
                {activeTab === 'cases' && (
                  <IncidentCaseManagement 
                    events={securityEvents} 
                    onSelectCase={setSelectedCase}
                    onRefresh={loadSecurityData}
                  />
                )}
                {activeTab === 'timeline' && (
                  <EventTimeline events={securityEvents} />
                )}
                {activeTab === 'threats' && (
                  <ThreatIntelligence events={securityEvents} />
                )}
                {activeTab === 'behavior' && (
                  <UserBehaviorAnalysis events={securityEvents} />
                )}
                {activeTab === 'evidence' && (
                  <EvidenceCollection selectedCase={selectedCase} />
                )}
                {activeTab === 'correlation' && (
                  <CorrelationAnalysis events={securityEvents} />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityEventInvestigationPortal;